/**
 * 사용 기록기의 순수한 부분 — DOM 도 네트워크도 모른다. 그래서 시험한다.
 *
 * 2026-09-29. 기록기(`recorder.ts`)가 브라우저에서 잡은 것을 사건 한 줄로 만들고,
 * 모아 두었다가 묶어 보내는 규칙이 여기 있다. 서버 계약은 `ErpService/events.go` 다.
 *
 * **이 파일은 아무것도 import 하지 않는다.** 시험을 번들러 없이 `node --test` 로 돌리려고
 * (Node 가 타입만 지우고 실행한다) 그렇게 둔다 — 매개변수 속성·enum 도 안 쓴다.
 */

export type EventKind =
  | 'open' | 'action' | 'field' | 'row' | 'cell' | 'rows' | 'tab' | 'notice' | 'error'
  | 'menu' | 'click' | 'file' | 'state' | 'visibility' | 'lookup';

/** 서버가 받는 사건 한 줄. `extra` 는 JSON **글자**다 — 서버는 풀지 않고 그대로 넣는다. */
export type RecordedEvent = {
  ts: number;
  actor: 'human' | 'agent';
  route: string;
  kind: EventKind;
  target: string;
  value: string;
  extra: string;
};

/** 서버가 자르는 한도와 같다. 여기서 먼저 잘라 보내는 양을 줄인다. */
export const MAX_VALUE = 4 * 1024;
export const MAX_EXTRA = 16 * 1024;
/** 서버가 한 번에 받는 사건 수. 넘으면 서버가 뒤를 버린다. */
export const MAX_BATCH = 500;
/**
 * `fetch(keepalive)` 와 `sendBeacon` 은 본문이 64KB 를 넘으면 보내지 않는다(브라우저 한도).
 * 여유를 두고 60KB 로 자른다.
 */
export const KEEPALIVE_BYTES = 60 * 1024;
/** 보통 보낼 때 한 요청의 크기. 서버 한도(4MB)보다 한참 작게 둔다 — 느린 회선에서 한 번에 한 덩이. */
export const NORMAL_BYTES = 512 * 1024;

/**
 * 비밀번호·자격증명으로 볼 이름. **값을 절대 남기지 않는다.**
 *
 * 사용자는 「값은 최대한 다 남긴다」로 정했지만 비밀번호만은 예외다 — 사용 기록은
 * CS 담당자와 학습 쪽이 읽는다. 이름이 애매하면 남기지 않는 쪽으로 기운다.
 */
const SECRET = /pw|passwd|password|secret|token/i;

export function isSecretKey(key: string | null | undefined): boolean {
  return Boolean(key) && SECRET.test(String(key));
}

export function clip(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max);
}

/**
 * 값을 글자로. 객체가 오면 JSON 으로 — 배열은 길이만 본다(표 통째를 싣지 않는다).
 */
export function valueText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.length}]`;
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return '';
  }
}

/**
 * `extra` 를 JSON 글자로 만든다.
 *
 * **서버는 16KB 에서 글자를 그냥 자른다** — JSON 한가운데서 잘리면 읽을 수 없는 줄이
 * 남는다. 그래서 넘칠 것 같으면 여기서 값마다 줄여 다시 만들고, 그래도 넘치면 앞부분만
 * 문자열로 싸서 보낸다. 어느 쪽이든 받은 쪽이 `JSON.parse` 할 수 있다.
 */
export function extraJson(extra: Record<string, unknown> | undefined): string {
  if (!extra) return '';
  const keys = Object.keys(extra).filter((key) => extra[key] !== undefined);
  if (!keys.length) return '';
  let text = '';
  try {
    text = JSON.stringify(extra);
  } catch {
    return '';
  }
  if (text.length <= MAX_EXTRA) return text;
  const shrunk: Record<string, unknown> = {};
  keys.forEach((key) => {
    const one = extra[key];
    shrunk[key] = typeof one === 'string' ? clip(one, 2000) : one;
  });
  try {
    text = JSON.stringify(shrunk);
  } catch {
    text = '';
  }
  if (text.length <= MAX_EXTRA) return text;
  return JSON.stringify({ truncated: true, head: clip(text, MAX_EXTRA - 100) });
}

/**
 * 누가 했나. 에이전트가 도는 동안의 조작은 에이전트 것이다 — 드라이버의 합성 클릭도
 * 사람 클릭과 똑같이 문서 수준 리스너에 걸리기 때문에 이것 없이는 둘이 섞인다.
 * 판단 함수가 없거나 던지면 사람으로 본다.
 */
export function actorOf(busy: (() => boolean) | undefined): 'human' | 'agent' {
  try {
    return busy?.() ? 'agent' : 'human';
  } catch {
    return 'human';
  }
}

export type EventInput = {
  kind: EventKind;
  target?: string | null;
  value?: unknown;
  extra?: Record<string, unknown>;
  ts?: number;
};

/**
 * 사건 한 줄을 만든다. 비밀로 볼 대상이면 **값과 extra 를 비운다**(사건 자체는 남긴다 —
 * 비밀번호 칸을 만졌다는 사실은 CS 에 쓸모가 있다).
 */
export function buildEvent(
  input: EventInput,
  context: { route: string; actor: 'human' | 'agent'; now: number; seq?: number },
): RecordedEvent {
  const target = String(input.target ?? '');
  const secret = isSecretKey(target);
  // 순번과 기록기 판은 **모든** 줄에 싣는다 — 비밀 칸이라 값을 비운 줄도 순서는 남아야 한다.
  const stamp = context.seq === undefined ? undefined : { seq: context.seq, v: RECORDER_VERSION };
  let extra = '';
  if (secret) extra = extraJson(stamp);
  else extra = extraJson(stamp ? { ...input.extra, ...stamp } : input.extra);
  return {
    ts: input.ts ?? context.now,
    actor: context.actor,
    // 서버는 경로가 빈 줄을 버린다. 화면이 안 열린 자리(메인)는 '/' 로 적는다.
    route: context.route || '/',
    kind: input.kind,
    target: clip(target, 256),
    value: secret ? '' : clip(valueText(input.value), MAX_VALUE),
    extra,
  };
}

/**
 * 기록기 판. 분석이 판마다 다른 규칙으로 찍힌 줄을 가를 수 있게 모든 줄의 `extra.v` 에 싣는다.
 *
 * - 1(표시 없음): 2026-09-29 첫 배포.
 * - 2: 칸 사건의 원인(`cause`)·전체 경로(`path`), 표 `cell` 신호, 팝업 `lookup`, 세션 순번(`seq`).
 */
export const RECORDER_VERSION = '2';

/**
 * 세션 순번의 시작값. 같은 탭을 새로고침해도 순번이 거꾸로 가지 않게 sessionStorage 에 둔
 * 마지막 값 다음부터 센다. 읽은 글자가 숫자가 아니면 0 부터.
 */
export function seqStart(kept: string | null | undefined): number {
  if (kept === null || kept === undefined || kept === '') return 0;
  const number = Number(kept);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) + 1 : 0;
}

/**
 * 보내기 전에 모아 두는 곳. 한도를 넘으면 **오래된 것부터** 버린다 — 서버가 죽어 있어도
 * 브라우저 메모리가 끝없이 늘지 않게.
 */
export class EventBuffer {
  readonly max: number;

  private items: RecordedEvent[] = [];

  dropped = 0;

  constructor(max = 2000) {
    this.max = max;
  }

  get size(): number {
    return this.items.length;
  }

  push(event: RecordedEvent): void {
    this.items.push(event);
    if (this.items.length > this.max) {
      const over = this.items.length - this.max;
      this.items.splice(0, over);
      this.dropped += over;
    }
  }

  /**
   * 앞에서부터 한 덩이를 꺼낸다. 수는 `maxCount`, 본문 크기는 `maxBytes` 를 넘지 않는다.
   * 사건 하나가 혼자서 한도를 넘으면 그 하나만 꺼낸다 — 영원히 막히지 않게.
   */
  take(maxCount = MAX_BATCH, maxBytes = NORMAL_BYTES): RecordedEvent[] {
    let bytes = 0;
    let count = 0;
    while (count < this.items.length && count < maxCount) {
      const size = eventBytes(this.items[count]);
      if (count > 0 && bytes + size > maxBytes) break;
      bytes += size;
      count += 1;
    }
    return this.items.splice(0, count);
  }

  /**
   * **못 보낸 덩이를 앞에 되돌려 놓는다.** 보내다 실패한 것을 버리면 그 구간이 통째로 빈다 — 15일 기록에서
   * 세션 18.5%의 번호가 끊겼고(빠진 것 6.5%), 끊김 직전은 대개 탭 숨김이었다(2차 연구 B-2). 한도는 그대로
   * 지킨다 — 넘치면 오래된 것부터 버리고 `dropped` 로 센다.
   */
  putBack(events: RecordedEvent[]): void {
    if (!events.length) return;
    this.items = [...events, ...this.items];
    if (this.items.length > this.max) {
      const over = this.items.length - this.max;
      this.items.splice(0, over);
      this.dropped += over;
    }
  }

  clear(): void {
    this.items = [];
  }
}

/**
 * 본문에서 사건 하나가 차지할 크기의 어림. UTF-8 에서 한글은 3바이트라 글자 수에 3을
 * 곱한다 — 모자라게 재서 64KB 를 넘기느니 넉넉하게 잰다.
 */
export function eventBytes(event: RecordedEvent): number {
  return 64 + (event.route.length + event.target.length + event.value.length + event.extra.length) * 3;
}

/** 모인 수가 이만큼이면 5초를 기다리지 않고 보낸다. */
export const FLUSH_COUNT = 50;

export function shouldFlushNow(size: number): boolean {
  return size >= FLUSH_COUNT;
}

/** 보낼 본문. 인증 미들웨어가 `window` 와 `token` 을 본문에서 읽는다. */
export function batchBody(
  caller: { custcd: string; perid: string; token: string },
  session: string,
  events: RecordedEvent[],
): string {
  return JSON.stringify({
    window: 'w_xusers', ...caller, session, events,
  });
}

export type Scalar = string | number | boolean | null;

/**
 * 화면 상태를 비교할 수 있는 납작한 모양으로. 값은 스칼라만 — 배열은 **길이만**
 * (`#key` 로), 객체는 한 단계만 펼친다(`data.cltcd`). 상세를 `state.data` 에 두는
 * 화면이 많다. 비밀로 볼 이름은 아예 안 넣는다.
 */
export function flattenState(state: unknown): Record<string, Scalar> {
  const flat: Record<string, Scalar> = {};
  if (!state || typeof state !== 'object') return flat;
  const record = state as Record<string, unknown>;
  Object.keys(record).forEach((key) => {
    if (isSecretKey(key)) return;
    const value = record[key];
    if (Array.isArray(value)) {
      flat[`#${key}`] = value.length;
    } else if (isScalar(value)) {
      flat[key] = value === undefined ? null : value as Scalar;
    } else if (value && typeof value === 'object' && isPlain(value)) {
      const inner = value as Record<string, unknown>;
      Object.keys(inner).forEach((sub) => {
        if (isSecretKey(sub)) return;
        const one = inner[sub];
        if (isScalar(one)) flat[`${key}.${sub}`] = one === undefined ? null : one as Scalar;
      });
    }
  });
  return flat;
}

function isScalar(value: unknown): boolean {
  return value === null || value === undefined
    || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * 한 단계 펼칠 객체인가. 평범한 객체와 **화면 모델 인스턴스**(`new DepositInputModel(...)`)다.
 * 2판에서 모델도 펼친다 — 입금입력처럼 머리 칸을 모델에 두는 화면은 칸 값이 하나도 안 보였다.
 * 날짜·Map·DOM·ref 같은 것은 뺀다.
 */
function isPlain(value: object): boolean {
  // React ref(`{ current }`)는 화면 값이 아니다.
  if ('current' in value && Object.keys(value).length === 1) return false;
  const proto = Object.getPrototypeOf(value);
  if (proto === Object.prototype || proto === null) return true;
  if (value instanceof Date || value instanceof RegExp || value instanceof Promise) return false;
  if (typeof Map !== 'undefined' && (value instanceof Map || value instanceof Set)) return false;
  if (typeof Node !== 'undefined' && value instanceof Node) return false;
  return true;
}

export type StateChange = { key: string; before: Scalar | undefined; after: Scalar | undefined };

/** 두 납작한 상태에서 바뀐 것. 새로 생긴 키도, 사라진 키도 바뀐 것이다. */
export function diffState(
  before: Record<string, Scalar>,
  after: Record<string, Scalar>,
): StateChange[] {
  const changes: StateChange[] = [];
  const seen: Record<string, true> = {};
  Object.keys(after).forEach((key) => {
    seen[key] = true;
    if (!(key in before) || before[key] !== after[key]) {
      changes.push({ key, before: before[key], after: after[key] });
    }
  });
  Object.keys(before).forEach((key) => {
    if (!seen[key]) changes.push({ key, before: before[key], after: undefined });
  });
  return changes;
}

/** `data.cltcd` → `cltcd`. 앵커(`data-el-field`)는 안쪽 이름을 쓴다. */
export function leafKey(key: string): string {
  const dot = key.lastIndexOf('.');
  return dot < 0 ? key : key.slice(dot + 1);
}

/**
 * 코드 칸(`cltcd`)과 짝이 되는 이름 칸의 값. 같은 층(같은 객체)에서만 찾는다.
 * 드라이버의 `pairedName` 과 같은 접미사 규칙이다 — 드라이버는 동기화되는 코드라
 * 여기서 가져다 쓰지 않는다.
 */
export function pairedNameOf(key: string, flat: Record<string, Scalar>): string | undefined {
  const dot = key.lastIndexOf('.');
  const prefix = dot < 0 ? '' : key.slice(0, dot + 1);
  const leaf = leafKey(key);
  const stem = leaf.replace(/(cd|id|code)$/i, '');
  if (stem === leaf) return undefined;
  const candidate = ['nm', 'name'].map((suffix) => `${prefix}${stem}${suffix}`)
    .find((one) => one in flat);
  return candidate === undefined ? undefined : valueText(flat[candidate]);
}

/** 행 하나를 사람이 읽을 짧은 글자로. 칸 사이 공백을 하나로 줄인다. */
export function rowLabel(text: string | null | undefined, max = 160): string {
  return clip(String(text ?? '').replace(/\s+/g, ' ').trim(), max);
}

/**
 * 앵커(`data-el-field`) 없는 칸의 이름을 **곁의 라벨 글자**로 짓는다 — `label:점검일자`.
 *
 * 모달 안 입력은 앵커도 화면 상태도 없는 일이 많다. 점검계획 상세 모달처럼 운영에서 가장 많이
 * 쓰는 일이 거기서 일어나는데, 1판 기록에는 모달을 여닫은 것만 남았다(2026-09-30). 비밀번호로
 * 보이는 라벨은 이름도 만들지 않는다.
 */
export function labelFieldKey(label: string | null | undefined): string | null {
  const text = clip(String(label ?? '').replace(/\s+/g, ' ').replace(/^[\s*:·]+|[\s*:·]+$/g, '').trim(), 30);
  if (!text || isSecretKey(text) || /비밀번호|암호/.test(text)) return null;
  return `label:${text}`;
}

/** 한 페이지 로드(같은 탭의 새로고침 포함)를 묶는 id. */
export function newSessionId(): string {
  try {
    const cryptoLike = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (cryptoLike?.randomUUID) return cryptoLike.randomUUID();
  } catch {
    // 아래로
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * 같은 오류가 폭주하면(렌더 루프 안의 예외) 기록이 오류로 가득 찬다. 같은 글은 1분에
 * 한 번, 전체는 1분에 20건까지만 남긴다.
 */
export type ErrorGate = { allow(message: string, now: number): boolean };

export function errorGate(): ErrorGate {
  let seen: Record<string, number> = {};
  let windowStart = 0;
  let count = 0;
  return {
    allow(message: string, now: number): boolean {
      if (now - windowStart > 60000) {
        windowStart = now;
        count = 0;
        seen = {};
      }
      if (seen[message] !== undefined) return false;
      if (count >= 20) return false;
      seen[message] = now;
      count += 1;
      return true;
    },
  };
}

// --- 2판: 원인·표 칸·팝업 ------------------------------------------------------

/**
 * 칸 값이 왜 바뀌었나. **사람이 확정한 값은 `input`·`popup` 뿐이다** — 재현·학습은 이 둘만
 * 입력으로 쓴다. 나머지는 화면이 채운 값이다(폼 열기·행 선택·신규·조회·계산).
 *
 * 2026-09-29 첫 배포 자료에서 칸 사건 869건 중 665건이 클릭 한 번 뒤 50ms 안에 5개 이상
 * 몰려 찍혔다. 폼이 열리며 채운 값을 사람이 친 것으로 적고 있었다.
 */
export type Cause = 'input' | 'popup' | 'row' | 'new' | 'retrieve' | 'auto';

/** 마지막으로 사람이 한 일 — 칸을 직접 만지지 않은 변화의 원인을 이것으로 적는다. */
export type Trigger = {
  kind: 'none' | 'input' | 'row' | 'action' | 'nav' | 'dialog' | 'popup';
  /** 누른 단추(`data-el-action` 의 id 또는 단추 글자). */
  action?: string;
};

export function causeOfTrigger(trigger: Trigger): Cause {
  if (trigger.kind === 'row') return 'row';
  if (trigger.kind === 'action') {
    const id = String(trigger.action ?? '').toUpperCase();
    if (/NEW|ADD|INSERT|COPY|신규|추가|복사/.test(id)) return 'new';
    if (/RETRIEVE|SEARCH|REFRESH|조회|검색/.test(id)) return 'retrieve';
  }
  return 'auto';
}

/** 쉼표·하이픈·공백을 뺀 글자 — `1,000` 과 `1000`, `2026-09-01` 과 `20260901` 을 같게 본다. */
export function looseText(value: unknown): string {
  return valueText(value).replace(/[\s,\-/.:]/g, '');
}

export type FieldJudge = {
  leaf: string;
  value: string;
  /** 지난 비교 뒤로 사람이 이 칸(앵커)을 만졌다 — 입력·변경·클릭·포커스. */
  touched: boolean;
  /** 이번 비교에서 **같은 끝 이름**으로 바뀐 다른 경로 수(`actInfo.actcd` 와 `unitFocused.actcd`). */
  rivals: number;
  /** 앵커가 지금 보여 주는 값. 모르면 undefined. */
  domValue?: string;
  /** 방금 고른 팝업. `field` 는 팝업을 연 칸, `values` 는 고른 행의 값들. */
  lookup?: { field?: string; values: string[] } | null;
  trigger: Trigger;
};

export function fieldCause(judge: FieldJudge): Cause {
  let cause: Cause = causeOfTrigger(judge.trigger);
  const { lookup } = judge;
  if (lookup && ((lookup.field && lookup.field === judge.leaf)
    || (judge.value !== '' && lookup.values.indexOf(judge.value) >= 0))) {
    cause = 'popup';
  } else if (judge.touched) {
    cause = 'input';
  }
  // 같은 끝 이름이 여러 경로에서 바뀌었으면 화면에 보이는 값과 같은 쪽만 사람 것이다.
  if ((cause === 'input' || cause === 'popup') && judge.rivals > 0 && judge.domValue !== undefined
    && looseText(judge.domValue) !== looseText(judge.value)) {
    return 'auto';
  }
  return cause;
}

/** 팝업에서 고른 행의 값들. 짧은 값(`0`·`1`)은 다른 칸과 우연히 겹치므로 뺀다. */
export function lookupValues(item: unknown, max = 12): string[] {
  if (!item || typeof item !== 'object') return [];
  const record = item as Record<string, unknown>;
  const values: string[] = [];
  Object.keys(record).forEach((key) => {
    if (values.length >= max || isSecretKey(key)) return;
    const one = record[key];
    if (!isScalar(one)) return;
    const text = valueText(one).trim();
    if (text.length >= 2 && values.indexOf(text) < 0) values.push(text);
  });
  return values;
}

/** 고른 행을 사건에 싣는 모양 — 빈 값을 빼고 짧게. */
export function lookupItem(item: unknown, max = 8): Record<string, string> {
  const out: Record<string, string> = {};
  if (!item || typeof item !== 'object') return out;
  const record = item as Record<string, unknown>;
  let count = 0;
  Object.keys(record).forEach((key) => {
    if (count >= max || isSecretKey(key)) return;
    const one = record[key];
    if (!isScalar(one) || valueText(one).trim() === '') return;
    out[key] = clip(valueText(one), 120);
    count += 1;
  });
  return out;
}

const ROW_KEY_ORDER = [/date$/i, /(num|no|seq)$/i, /(cd|code)$/i, /(nm|name)$/i];
const ROW_KEY_SKIP = /^(chk|new|isnew|custcd|spjangcd)$/i;

/**
 * 행을 사람이 알아볼 업무 키로. 번호(인덱스)는 정렬·조회마다 바뀐다 — 「9월 1일 3번
 * 미수, 덕봉빌딩」처럼 날짜·번호·코드·이름 순으로 빈 값이 아닌 것을 몇 개 고른다.
 * 이번에 바꾼 키(`exclude`)는 행을 가리키는 값이 아니라 바꾼 값이라 뺀다.
 */
export function rowIdentity(row: unknown, exclude: string[] = [], max = 4): Record<string, string> {
  const out: Record<string, string> = {};
  if (!row || typeof row !== 'object') return out;
  const record = row as Record<string, unknown>;
  const keys = Object.keys(record).filter((key) => !isSecretKey(key)
    && !ROW_KEY_SKIP.test(key)
    && exclude.indexOf(key) < 0
    && isScalar(record[key])
    && valueText(record[key]).trim() !== '');
  // 종류마다 하나씩 먼저 고른다(날짜만 넷이 뽑히지 않게). 자리가 남으면 다음 것.
  const byKind = ROW_KEY_ORDER.map((pattern) => keys.filter((key) => pattern.test(key)));
  const picked: string[] = [];
  for (let round = 0; round < max; round += 1) {
    for (let kind = 0; kind < byKind.length; kind += 1) {
      const next = byKind[kind][round];
      if (next !== undefined && picked.length < max && picked.indexOf(next) < 0) picked.push(next);
    }
  }
  picked.forEach((key) => { out[key] = clip(valueText(record[key]).trim(), 60); });
  return out;
}

/** 체크칸 값인가 — `'1'/'0'`, `true/false`, `'Y'/'N'`. */
export function isCheckValue(value: unknown): boolean {
  return value === true || value === false || value === '1' || value === '0'
    || value === 'Y' || value === 'N';
}

export function isChecked(value: unknown): boolean {
  return value === true || value === '1' || value === 'Y';
}

export type Gesture = { kind: 'check' | 'key' | 'pointer' | 'input'; at: number; cell?: string };

/**
 * 표 칸 변경의 출처. 부품 신호(`elman:cell`)는 「바뀌었다」만 알리므로 사람이 무엇을 하던
 * 중이었나로 가른다. 팝업을 방금 골랐으면 popup, 그 칸에 글을 치고 있었으면 input,
 * 체크칸을 방금 눌렀으면 check, 아무 손길 없이 바뀌었으면 auto(계산·연쇄 갱신).
 */
export function cellSource(judge: {
  now: number; cell: string; gesture?: Gesture | null; lookupAt?: number;
}): string {
  const { now, gesture } = judge;
  if (judge.lookupAt !== undefined && now - judge.lookupAt < 3000) return 'popup';
  if (gesture && gesture.cell === judge.cell && gesture.kind === 'input' && now - gesture.at < 30000) return 'input';
  if (gesture && now - gesture.at < 1500) {
    if (gesture.kind === 'check') return 'check';
    if (gesture.kind === 'key' || gesture.kind === 'input') return 'input';
    return 'click';
  }
  return 'auto';
}

const SOURCE_RANK: Record<string, number> = {
  popup: 5, check: 4, input: 3, click: 2, auto: 1,
};

/** 아직 안 보낸 칸 변경 하나. 같은 칸의 연속 변경(글자마다 부르는 칸)은 하나로 합친다. */
export type CellCommit = {
  grid: string;
  row: string;
  column: string;
  changes: Record<string, { before: Scalar; after: Scalar }>;
  source: string;
  /** 부품 신호(자료 기준)에서 왔나. 아니면 DOM 에서 본 것이다. */
  hook: boolean;
  rowKey?: Record<string, string>;
  label?: string;
  lookupId?: string;
};

export function cellKey(grid: string, row: string, column: string): string {
  return `${grid}\u0001${row}\u0001${column}`;
}

export function toScalar(value: unknown): Scalar {
  if (value === undefined) return null;
  if (isScalar(value)) return value as Scalar;
  return clip(valueText(value), 300);
}

export function mergeCell(prev: CellCommit | undefined, next: CellCommit): CellCommit {
  if (!prev) return next;
  const changes: CellCommit['changes'] = { ...prev.changes };
  Object.keys(next.changes).forEach((key) => {
    const old = changes[key];
    // 이전 값은 먼저 온 것 — 단, DOM 은 이전 값을 모르므로 부품 신호가 늦게 왔으면 신호 쪽을 쓴다.
    const keepOld = old && (prev.hook || !next.hook);
    changes[key] = { before: keepOld ? old.before : next.changes[key].before, after: next.changes[key].after };
  });
  const base = next.hook || !prev.hook ? next : prev;
  const source = (SOURCE_RANK[prev.source] ?? 0) > (SOURCE_RANK[next.source] ?? 0) ? prev.source : next.source;
  return {
    ...base,
    changes,
    source,
    hook: prev.hook || next.hook,
    rowKey: base.rowKey ?? prev.rowKey ?? next.rowKey,
    label: base.label ?? prev.label ?? next.label,
    lookupId: next.lookupId ?? prev.lookupId,
  };
}

/** 합친 뒤 실제로 달라진 키만. 하나도 없으면(쳤다 지움) null — 사건을 안 남긴다. */
export function settledCell(commit: CellCommit): CellCommit | null {
  const changes: CellCommit['changes'] = {};
  let count = 0;
  Object.keys(commit.changes).forEach((key) => {
    if (isSecretKey(key)) return;
    const one = commit.changes[key];
    if (valueText(one.before) === valueText(one.after)) return;
    changes[key] = one;
    count += 1;
  });
  return count ? { ...commit, changes } : null;
}
